import { Box, Typography, Paper, alpha } from "@mui/material";
import { useTheme } from "@mui/system";
import { useTranslation } from "react-i18next";

/**
 * QR Camera component for scanning QR codes
 * @param {Object} props - Component props
 * @param {Function} props.onCameraInit - Callback to initialize camera
 * @param {boolean} props.videoReady - Whether video is ready
 * @param {boolean} props.scanned - Whether QR code has been scanned
 * @param {string} props.cameraError - Camera error message if any
 * @returns {JSX.Element} QR camera component
 */
export const QRCamera = ({
  onCameraInit,
  videoReady,
  scanned,
  cameraError,
}) => {
  const { t } = useTranslation("common");
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        overflow: "hidden",
        borderRadius: "12px",
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        position: "relative",
        width: "100%",
      }}
    >
      <Box
        id="qr-reader"
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          minHeight: 360,
          maxHeight: 520,
          background: alpha(theme.palette.divider, 0.05),
          position: "relative",
          overflow: "hidden",
        }}
        ref={(el) => {
          if (el && onCameraInit) {
            onCameraInit(el);
          }
        }}
      >
        {/* Camera gets mounted here */}

        {/* QR targeting frame overlay */}
        {videoReady && !scanned && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: "50%",
                height: "75%",
                border: `2px dashed rgba(255,255,255,0.5)`,
                borderRadius: 1,
                boxShadow: "0 0 0 1000px rgba(0,0,0,0.3)",
              }}
            />
          </Box>
        )}
      </Box>

      {!videoReady && !cameraError && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.background.default, 0.6),
            backdropFilter: "blur(2px)",
            color: theme.palette.text.secondary,
            fontSize: 14,
          }}
        >
          {t("common.initializing_camera", "Initializing camera...")}
        </Box>
      )}

      {cameraError && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.background.default, 0.9),
            color: theme.palette.error.main,
            p: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {t("common.camera_unavailable", "Camera unavailable")}
          </Typography>
          <Typography variant="caption" sx={{ mb: 1 }}>
            {cameraError}
          </Typography>
          <Typography variant="caption">
            {t(
              "common.camera_help_text",
              "Ensure you have granted camera permission, no other tab is using it, and you're using HTTPS."
            )}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
